import { useInstantSearch } from "react-instantsearch";
import {
  MutableRefObject,
  startTransition,
  useEffect,
  useRef,
  useState
} from "react";
import {
  TagItem,
  tagItem
} from "components/FindYourCruise/Components/Autocomplete/tagItem";
import { match } from "ts-pattern";
import { stringPresent } from "src/utils/strings";
import { facetSources, FacetSource, PublicFacetSource } from "./buildSources";
import { Language } from "src/utils/localization/languages/types";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import {
  localizedFacetSource,
  unlocalizedFacetSource
} from "components/Algolia/utils";
import { isPresent } from "src/utils/checks";

type IndexUiState = ReturnType<typeof useInstantSearch>["indexUiState"];

type IndexUIState = ReturnType<typeof useInstantSearch>["indexUiState"];

export type AddFacetFilter = ReturnType<
  typeof useAutocompleteSearch
>["addFacetFilter"];

export type CurrentFacetsFilters = ReturnType<
  typeof useAutocompleteSearch
>["currentFacetsFilters"];

export type CurrentFacetsFiltersResult = ReturnType<CurrentFacetsFilters>;

const facetValues = (state: IndexUIState, facetName: FacetSource) =>
  state.refinementList?.[facetName] || [];

const tagsFrom = (
  language: Language,
  indexUiStateRef: MutableRefObject<IndexUIState>
) =>
  facetSources.flatMap((facetName) => {
    const localizedFacetName = localizedFacetSource<
      PublicFacetSource,
      FacetSource
    >(language, facetName);
    return facetValues(indexUiStateRef.current, localizedFacetName).map((x) =>
      tagItem(localizedFacetName, x)
    );
  });

export const useAutocompleteSearch = () => {
  const language = useLanguage();
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const indexUiStateRef = useRef(indexUiState);
  const [tags, setTags] = useState<TagItem[]>(
    tagsFrom(language, indexUiStateRef)
  );

  useEffect(() => {
    indexUiStateRef.current = indexUiState;
    startTransition(() => {
      setTags(tagsFrom(language, indexUiStateRef));
    });
  }, [language, indexUiState]);

  const setIndexUiStateAll = (fn: (prev: IndexUiState) => IndexUiState) => {
    setIndexUiState((prev) => {
      const newState = fn(prev);
      indexUiStateRef.current = newState;
      return newState;
    });
  };

  const addFacetFilter = (facetName: FacetSource, value: string) => {
    setIndexUiStateAll((prev) => {
      if (facetValues(prev, facetName).includes(value)) return prev;

      return {
        ...prev,
        page: undefined, //reset pagination
        refinementList: {
          ...prev.refinementList,
          [facetName]: [...facetValues(prev, facetName), value]
        }
      };
    });
  };

  const removeFacetFilter = (facetName: FacetSource, value: string) => {
    setIndexUiStateAll((prev) => ({
      ...prev,
      page: undefined, //reset pagination
      refinementList: {
        ...prev.refinementList,
        [facetName]: facetValues(prev, facetName).filter((x) => x != value)
      }
    }));
  };

  const facetFilter = (facetName: FacetSource, facetValues: string[]) => {
    const result = facetValues
      .map((value) => `${facetName}:"${value}"`)
      .join(conjunctionForFacet(facetName));
    return `(${result})`;
  };

  const conjunctionForFacet = (facetName: FacetSource) =>
    match(unlocalizedFacetSource(facetName))
      .with("portNames", "countryNames", () => " AND ")
      .otherwise(() => " OR ");

  const currentFacetsFilters = (excludedFacets: PublicFacetSource[]) => {
    const facets = (indexUiStateRef.current.refinementList || {}) as Record<
      FacetSource,
      string[]
    >;

    const facetFilters = (Object.keys(facets) as FacetSource[])
      .filter(isPresent)
      .filter((x) => !excludedFacets.includes(unlocalizedFacetSource(x)))
      .flatMap((facetName) => ({ name: facetName, value: facets[facetName] }))
      .map((pair) => facetFilter(pair.name, pair.value))
      .filter((x) => !!x)
      .join(" AND ");

    const filter = [facetFilters, indexUiStateRef.current.configure?.filters]
      .filter(stringPresent)
      .join(" AND ");
    return { filter, facets };
  };

  return {
    addFacetFilter,
    removeFacetFilter,
    currentFacetsFilters,
    tags
  };
};
